import { Alert, SelectedSource, schAlertSeverity, schAlertStatus } from '@aloft/toolkit-shared';

import { get } from 'lodash';

import {
  CompPropertyDataState,
  PropertyDataState,
  SubjectPropertyDataState,
} from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';
import {
  getSubFeatureParentKey,
  isInconsistentFeatureField,
} from '../utils/inconsistentFeatureFields.ts';

const getPropertyName = (propertyId: string, fullCompSequence?: string[]): string =>
  propertyId === 'subject' ? 'Subject' : getComparableDisplayName(propertyId, fullCompSequence);

const getSelectedSources = (propertyDataEntity: PropertyDataState) =>
  propertyDataEntity.id === 'subject'
    ? (propertyDataEntity as SubjectPropertyDataState).subjectData.selectedSources
    : (propertyDataEntity as CompPropertyDataState).compData.selectedSources;

const propertyHasFeature = (
  propertyDataEntity: PropertyDataState,
  subFeatureKey: string,
  fieldName: string,
): boolean => {
  const isArvField = fieldName.startsWith('editableData.arv.');

  if (isArvField) {
    if (propertyDataEntity.id !== 'subject') return false;
    const improvementsRoot = (propertyDataEntity as SubjectPropertyDataState).subjectData
      .editableData.arv?.improvements;
    return get(improvementsRoot, `${subFeatureKey}.hasFeature`) === true;
  }

  const improvementsRoot =
    propertyDataEntity.id === 'subject'
      ? (propertyDataEntity as SubjectPropertyDataState).subjectData.editableData.improvements
      : (propertyDataEntity as CompPropertyDataState).compData.editableData.improvements;

  return get(improvementsRoot, `${subFeatureKey}.hasFeature`) === true;
};

export const inconsistentFeature: AlertDefinition = ({
  propertyDataEntities,
  conflictedFields,
  compSequence,
  fullCompSequence,
  order,
  compType,
  propertyType,
}) => {
  const triggers = getAlertTriggers({ fixRoute: 'features', order });

  let propertyIds = ['subject', ...compSequence];
  if (propertyType === 'subject') {
    propertyIds = ['subject'];
  } else if (propertyType === 'comp') {
    propertyIds = compSequence;
  }

  return Object.values(conflictedFields)
    .filter((conflict) => {
      if (!isInconsistentFeatureField(conflict.fieldName, conflict.propertyId)) return false;
      if (!propertyIds.includes(conflict.propertyId)) return false;

      const propertyDataEntity = propertyDataEntities[conflict.propertyId];
      if (!propertyDataEntity) return false;

      const subFeatureKey = getSubFeatureParentKey(conflict.fieldName);
      if (
        subFeatureKey &&
        !propertyHasFeature(propertyDataEntity, subFeatureKey, conflict.fieldName)
      ) {
        return false;
      }

      return true;
    })
    .map((conflict) => {
      const propertyName = getPropertyName(conflict.propertyId, fullCompSequence);
      const selectedSources = getSelectedSources(
        propertyDataEntities[conflict.propertyId] as PropertyDataState,
      );
      const conflictIsResolved = (
        get(selectedSources, conflict.fieldName) as SelectedSource | undefined
      )?.conflictIsResolved;

      return {
        type: getScopedAlertType('inconsistentFeature', compType),
        message: `${propertyName} features appear inconsistent across data sources`,
        severity: schAlertSeverity.enum.warning,
        status: conflictIsResolved
          ? schAlertStatus.enum.dismissed
          : schAlertStatus.enum.active,
        orderType: ['appraisify'],
        triggers,
        propertyID: conflict.propertyId,
        ...(compType && { compType }),
        fixingInfo: {
          fixingMethod: ['action'],
          commentAlwaysRequired: false,
          targetCellKey: `${conflict.propertyId}-${conflict.fieldName}`,
          targetCellTooltip: 'Click to verify or select a different value',
          pairedCostRouteItem: 'features',
          tips: 'Review all sources and reconcile the conflicts.',
        },
      } as Alert;
    });
};
